import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from './EmptyState.js';
import { LoadingState } from './LoadingState.js';
import { Button } from './Button.js';
import { ApiError } from '../../lib/api.js';

export interface ListStateProps {
  isLoading: boolean;
  isError?: boolean;
  error?: unknown;
  /** true cuando la consulta trajo cero elementos DE VERDAD. */
  isEmpty: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  onRetry?: () => void;
  /** Lo que se renderiza cuando hay datos. */
  children: ReactNode;
  size?: 'sm' | 'md';
}

/**
 * Estado de una lista, con la PRECEDENCIA correcta: cargando → error → vacío →
 * datos.
 *
 * Existe por un bug real (2026-08-18): el panel de cuentas se veía VACÍO cuando
 * el servidor devolvía 429. La pantalla solo miraba `isLoading` y `length===0`,
 * así que cualquier fallo se renderizaba idéntico a "no hay nada". Una lista
 * vacía por error es el peor mensaje posible: hace pensar que se perdieron los
 * datos.
 *
 * El orden acá no es estético, es la corrección: `isError` SIEMPRE gana sobre
 * `isEmpty`, porque con un error no se sabe si hay elementos o no.
 */
export function ListState({
  isLoading, isError, error, isEmpty,
  emptyTitle, emptyDescription, emptyAction, onRetry, children, size = 'md',
}: ListStateProps) {
  const { t } = useTranslation();

  if (isLoading) return <LoadingState size={size} />;

  if (isError || error) {
    const status = error instanceof ApiError ? error.status : undefined;
    // El 429 tiene su propio texto: no es un fallo, es "esperá". Decirle
    // "ocurrió un error" a alguien que solo tiene que aguardar un minuto manda
    // a investigar un problema que no existe.
    const isRateLimited = status === 429;
    const message = error instanceof ApiError ? error.message : null;
    return (
      <EmptyState
        size={size}
        reserveSpace
        title={isRateLimited
          ? t('common.listState.rateLimitedTitle', 'Hiciste muchas peticiones seguidas')
          : t('common.listState.errorTitle', 'No se pudo cargar la lista')}
        description={message ?? (isRateLimited
          ? t('common.listState.rateLimitedBody', 'Espera un momento y vuelve a intentar. No se perdió nada.')
          : t('common.listState.errorBody', 'Los datos siguen ahí — falló la consulta, no la información.'))}
        {...(onRetry ? {
          action: (
            <Button variant="outline" tone="cyan" size="sm" onClick={onRetry}>
              {t('common.retry', 'Reintentar')}
            </Button>
          ),
        } : {})}
      />
    );
  }

  if (isEmpty) {
    return (
      <EmptyState
        size={size}
        reserveSpace
        title={emptyTitle}
        {...(emptyDescription ? { description: emptyDescription } : {})}
        {...(emptyAction ? { action: emptyAction } : {})}
      />
    );
  }

  return <>{children}</>;
}
